import { useEffect, useState } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { getDefaultBreak } from "./weekSchedule.utils";

export interface ShiftTimesValue {
	from: string;
	to: string;
	break: number;
}

interface Props {
	/** Viewport position the popover is anchored to. Closed when undefined. */
	anchor?: { x: number; y: number };
	title: string;
	subtitle?: string;
	submitLabel: string;
	initialValue?: ShiftTimesValue;
	onSubmit: (value: ShiftTimesValue) => void;
	onClose: () => void;
}

const DEFAULT_VALUE: ShiftTimesValue = { from: "08:00", to: "17:00", break: 0.5 };

const inputClass =
	"h-9 rounded-md !border !border-solid !border-gray-300 bg-white !px-2 !text-sm text-gray-900 outline-none focus:border-[#219CD0]";

const ShiftTimesPopover = ({ anchor, title, subtitle, submitLabel, initialValue, onSubmit, onClose }: Props) => {
	const [value, setValue] = useState<ShiftTimesValue>(initialValue || DEFAULT_VALUE);
	const [isBreakTouched, setIsBreakTouched] = useState(false);

	useEffect(() => {
		if (!anchor) return;
		setValue(initialValue || DEFAULT_VALUE);
		setIsBreakTouched(!!initialValue);
	}, [anchor?.x, anchor?.y]);

	const setTime = (key: "from" | "to", time: string) =>
		setValue((prev) => {
			const next = { ...prev, [key]: time };
			if (!isBreakTouched && next.from && next.to) next.break = getDefaultBreak(next.from, next.to);
			return next;
		});

	const isValid = !!value.from && !!value.to && value.from !== value.to && value.break >= 0;

	return (
		<Popover open={!!anchor} onOpenChange={(open) => !open && onClose()}>
			<PopoverAnchor asChild>
				<span className="pointer-events-none fixed h-px w-px" style={{ left: anchor?.x ?? 0, top: anchor?.y ?? 0 }} />
			</PopoverAnchor>
			<PopoverContent align="start" className="w-80">
				<form
					className="flex flex-col gap-3"
					onSubmit={(e) => {
						e.preventDefault();
						if (isValid) onSubmit(value);
					}}
				>
					<div>
						<div className="text-sm font-semibold text-gray-900">{title}</div>
						{subtitle && <div className="text-xs text-gray-500 first-letter:uppercase">{subtitle}</div>}
					</div>
					<div className="flex items-end gap-3">
						<label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
							Från - Till
							<span className="flex items-center gap-1">
								<input
									type="time"
									autoFocus
									className={`${inputClass} w-24`}
									value={value.from}
									onChange={(e) => setTime("from", e.target.value)}
								/>
								<span>-</span>
								<input
									type="time"
									className={`${inputClass} w-24`}
									value={value.to}
									onChange={(e) => setTime("to", e.target.value)}
								/>
							</span>
						</label>
						<label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
							Rast (tim)
							<input
								type="number"
								min={0}
								step={0.25}
								className={`${inputClass} w-16`}
								value={value.break}
								onChange={(e) => {
									setIsBreakTouched(true);
									setValue((prev) => ({ ...prev, break: Number(e.target.value) }));
								}}
							/>
						</label>
					</div>
					<div className="flex items-center justify-between">
						<button type="button" className="cursor-pointer !text-sm !text-gray-500 underline" onClick={onClose}>
							Stäng
						</button>
						<button
							type="submit"
							disabled={!isValid}
							className="cursor-pointer rounded-md !bg-[#29A54D] !px-4 !py-1.5 !text-sm font-semibold !text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
						>
							{submitLabel}
						</button>
					</div>
				</form>
			</PopoverContent>
		</Popover>
	);
};

export default ShiftTimesPopover;
